/**
 * Current Season Hero Component for v2v2 SWFL Farmers Market Application
 * Large hero section showing current seasonal highlights
 */



import { useMemo } from 'react';
import {
  Leaf,
  Calendar,
  MapPin,
  ShoppingBag,
  Sun,
  ArrowRight,
  Flower2,
  Snowflake,
  CloudSun,
  Lightbulb
} from 'lucide-react';
import { useSeasonal, useUPickLocations } from '@/hooks/use-seasonal';
import { cn } from '@/lib/utils';

interface CurrentSeasonProps {
  className?: string;
  showTips?: boolean;
  showUPick?: boolean;
  showMarkets?: boolean;
}

export function CurrentSeason({
  className,
  showTips = true,
  showUPick = true,
  showMarkets = true,
}: CurrentSeasonProps) {
  const {
    currentMonth,
    currentSeason,
    currentMonthData,
    peakProduce,
    shoppingTips,
    allUPickFarms,
    allFarmersMarkets,
    isLoading,
  } = useSeasonal();

  const SeasonIcon = useMemo(() => {
    switch (currentSeason) {
      case 'spring': return <Flower2 className="h-8 w-8 text-pink-500" />;
      case 'summer': return <Sun className="h-8 w-8 text-yellow-500" />;
      case 'fall': return <CloudSun className="h-8 w-8 text-orange-500" />;
      case 'winter': return <Snowflake className="h-8 w-8 text-forest-light" />;
      default: return <Leaf className="h-8 w-8 text-green-500" />;
    }
  }, [currentSeason]);

  if (isLoading) {
    return (
      <div className={cn('animate-pulse', className)}>
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200', className)}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-4 right-4">
          <Leaf className="h-32 w-32 text-green-600 transform rotate-45" />
        </div>
        <div className="absolute bottom-4 left-4">
          <Sun className="h-24 w-24 text-yellow-500" />
        </div>
      </div>

      <div className="relative p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <Calendar className="h-5 w-5" />
              <span className="font-medium uppercase tracking-wide text-sm">
                {currentMonth} {new Date().getFullYear()}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-green-900 flex items-center gap-3">
              {SeasonIcon}
              What's In Season Now
            </h2>
            <p className="text-green-700 mt-2">
              {currentMonthData.highlights}
            </p>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-5xl font-bold text-green-200">
              {currentMonthData.totalPeak}
            </span>
            <span className="text-green-600 text-sm font-medium">
              Peak Items
            </span>
          </div>
        </div>

        {/* Peak Produce */}
        <div className="space-y-3">
          <h3 className="font-semibold text-green-800 flex items-center gap-2">
            <Leaf className="h-5 w-5" />
            Peak Season Produce
          </h3>
          <div className="flex flex-wrap gap-2">
            {peakProduce.map((item) => (
              <span
                key={item}
                className="px-4 py-2 rounded-full bg-white text-green-800 font-medium border border-green-300 shadow-sm"
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Shopping Tips */}
        {showTips && shoppingTips.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Shopping Tips
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {shoppingTips.slice(0, 2).map((tip, index) => (
                <div
                  key={index}
                  className="p-3 bg-white/60 rounded-lg border border-green-200"
                >
                  <p className="font-medium text-green-900">{tip.tip}</p>
                  <p className="text-sm text-green-700 mt-1">
                    {tip.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* U-Pick CTA */}
        {showUPick && allUPickFarms.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-green-100 rounded-xl">
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">
                  Visit Local U-Pick Farms
                </p>
                <p className="text-sm text-green-700">
                  {allUPickFarms.join(' • ')}
                </p>
              </div>
            </div>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors">
              Find Farms
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Farmers Markets */}
        {showMarkets && allFarmersMarkets.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Best Farmers Markets
            </h3>
            <div className="flex flex-wrap gap-2">
              {allFarmersMarkets.slice(0, 4).map((market) => (
                <span
                  key={market}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/80 text-green-800 text-sm border border-green-200"
                >
                  <MapPin className="h-3 w-3" />
                  {market}
                </span>
              ))}
              {allFarmersMarkets.length > 4 && (
                <span className="px-3 py-1.5 rounded-lg bg-white/50 text-green-700 text-sm">
                  +{allFarmersMarkets.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Market Tip */}
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800 flex items-start gap-2">
            <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span><strong>Tip:</strong> {currentMonthData.tips}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
